import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled from "styled-components";
import { useAuth } from "../contexts/AuthContext";
import { useNotification } from "../contexts/NotificationContext";
import { useProject } from "../contexts/ProjectContext";
import { useReport } from "../contexts/ReportContext";
import { useChat } from "../hooks/useChat";
import Button from "./Button";
import Select from "./Select";
import SchemaRenderer from "./SchemaRenderer";
import type {
  AssigneeSelectOption,
  MasterSelectOption,
} from "./SchemaRenderer";
import { INCIDENT_REPORT_RESPONSE_SCHEMA } from "../schema/incidentReportSchema";
import { IssueAPI } from "../api/issue";
import { TemplateAPI } from "../api/template";
import { EnvironmentAPI } from "../api/environment";
import { ProjectAPI } from "../api/project";
import {
  applyIssueValueCreatePipeline,
  buildProjectAssigneeResolution,
  extractAssigneeUserRows,
  findMissingRequiredIssueFields,
  formatMissingRequiredIssueFieldsMessageJa,
  mergeIncidentReportForDisplay,
  normalizeAssigneeUsersFromApi,
  parseJsonObject,
  pickReportValueForTemplate,
  resolveIssuePersonInChargeToAssigneeUserId,
  appendSpecificationReportFieldsToRemarks,
  retainTfactIssueCustomSchemaProperties,
  extractAssigneeUsersPayload,
  extractBuildVersionsFromApi,
  extractExecutionEnvironmentsFromApi,
  resolveReportValueToMasterName,
  parseIssueTemplateItems,
  buildTemplateFieldSelectOptionsMap,
  resolveReportValueToTemplateOptionId,
  ISSUE_TEMPLATE_SELECT_FIELD_IDS,
  TFACT_ENTITY_ID_RE,
  resolveMasterIds,
  TFACT_MASTER_UUID_REGEX,
} from "../utils/incidentReportKeys";
import type { AssigneeUser, ProjectMasterOption } from "../utils/incidentReportKeys";

const ReportContainer = styled.div`
  height: 100%;
  overflow-y: auto;
  padding: 1rem;
`;

const ReportHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const ReportForm = styled.form`
  height: calc(100% - 80px);
  overflow-y: auto;
`;

const EditHint = styled.p`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 1rem 0;
  line-height: 1.5;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.surface};
  transition: border-color ${({ theme }) => theme.transitions.base},
    box-shadow ${({ theme }) => theme.transitions.base};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  &[readonly] {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
    cursor: default;
    border-color: ${({ theme }) => theme.colors.borderLight};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 0.75rem 1rem;
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.surface};
  font-family: ${({ theme }) => theme.fonts.base};
  resize: vertical;
  transition: border-color ${({ theme }) => theme.transitions.base},
    box-shadow ${({ theme }) => theme.transitions.base};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  &[readonly] {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
    cursor: default;
    border-color: ${({ theme }) => theme.colors.borderLight};
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  padding: 2rem;
`;

const EmptyStateIcon = styled.div`
  width: 4rem;
  height: 4rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const EmptyStateTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const EmptyStateDescription = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  max-width: 400px;
  margin: 0;
`;

const ReportTab: React.FC = () => {
  const [reportFormat, setReportFormat] = useState("tfact");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const { addNotification } = useNotification();
  const { user: authUser } = useAuth();
  const { currentProject } = useProject();
  const { reportState, isAutoTriggeringFromUrl, hasChatStarted } = useReport();
  const { sendMessage } = useChat();

  const canShowReportFromChat = hasChatStarted || isAutoTriggeringFromUrl;

  /** チャット／API から渡るレポート（オブジェクトのときのみ編集対象） */
  const structuredReportSource =
    reportData || (canShowReportFromChat ? reportState.report : null);

  const mergedReportFingerprint = useMemo(() => {
    if (!structuredReportSource || typeof structuredReportSource !== "object") {
      return "";
    }
    try {
      return JSON.stringify(
        mergeIncidentReportForDisplay(
          structuredReportSource as Record<string, unknown>
        )
      );
    } catch {
      return `__invalid__${Date.now()}`;
    }
  }, [structuredReportSource, reportData, reportState.report, canShowReportFromChat]);

  const mergedReportForDisplay = useMemo(
    () =>
      structuredReportSource && typeof structuredReportSource === "object"
        ? mergeIncidentReportForDisplay(
            structuredReportSource as Record<string, unknown>
          )
        : null,
    [mergedReportFingerprint]
  );

  const [editableReport, setEditableReport] = useState<Record<
    string,
    unknown
  > | null>(null);

  const [assigneeUsers, setAssigneeUsers] = useState<AssigneeUser[]>([]);
  const [assigneesLoading, setAssigneesLoading] = useState(false);
  const [executionEnvironments, setExecutionEnvironments] = useState<
    ProjectMasterOption[]
  >([]);
  const [buildVersions, setBuildVersions] = useState<ProjectMasterOption[]>([]);
  const [executionEnvironmentsLoading, setExecutionEnvironmentsLoading] =
    useState(false);
  const [buildVersionsLoading, setBuildVersionsLoading] = useState(false);
  const [issueTemplateItems, setIssueTemplateItems] = useState<
    Record<string, unknown>[]
  >([]);
  const [issueTemplateLoading, setIssueTemplateLoading] = useState(false);

  /** レポートタブ表示時にプロジェクトマスタ・課題テンプレートを取得 */
  useEffect(() => {
    if (!currentProject?.id) {
      setAssigneeUsers([]);
      setExecutionEnvironments([]);
      setBuildVersions([]);
      setIssueTemplateItems([]);
      setAssigneesLoading(false);
      setExecutionEnvironmentsLoading(false);
      setBuildVersionsLoading(false);
      setIssueTemplateLoading(false);
      return;
    }
    let cancelled = false;
    setAssigneesLoading(true);
    setExecutionEnvironmentsLoading(true);
    setBuildVersionsLoading(true);
    setIssueTemplateLoading(true);
    Promise.all([
      ProjectAPI.getAssignees(currentProject.id),
      EnvironmentAPI.getExecutionEnvironments(currentProject.id),
      EnvironmentAPI.getBuildVersions(currentProject.id),
      TemplateAPI.getTemplate("issue", currentProject.id),
    ])
      .then(([assigneeRes, envRes, buildRes, templateRes]) => {
        if (cancelled) return;
        if (assigneeRes.success && assigneeRes.data) {
          setAssigneeUsers(
            normalizeAssigneeUsersFromApi(
              extractAssigneeUsersPayload(assigneeRes.data)
            )
          );
        } else {
          setAssigneeUsers([]);
        }
        if (envRes.success && envRes.data) {
          setExecutionEnvironments(
            extractExecutionEnvironmentsFromApi(envRes.data)
          );
        } else {
          setExecutionEnvironments([]);
        }
        if (buildRes.success && buildRes.data) {
          setBuildVersions(extractBuildVersionsFromApi(buildRes.data));
        } else {
          setBuildVersions([]);
        }
        if (templateRes.success && templateRes.data) {
          setIssueTemplateItems(parseIssueTemplateItems(templateRes.data));
        } else {
          setIssueTemplateItems([]);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAssigneeUsers([]);
          setExecutionEnvironments([]);
          setBuildVersions([]);
          setIssueTemplateItems([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setAssigneesLoading(false);
          setExecutionEnvironmentsLoading(false);
          setBuildVersionsLoading(false);
          setIssueTemplateLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [currentProject?.id]);

  const assigneeSelectOptions = useMemo((): AssigneeSelectOption[] => {
    return [...assigneeUsers]
      .sort((a, b) =>
        (a.name || a.id).localeCompare(b.name || b.id, "ja", {
          sensitivity: "base",
        })
      )
      .map((u) => ({ value: u.id, label: u.name || u.id }));
  }, [assigneeUsers]);

  const executionEnvironmentSelectOptions = useMemo((): MasterSelectOption[] => {
    return executionEnvironments.map((m) => ({
      value: m.name,
      label: m.name,
    }));
  }, [executionEnvironments]);

  const buildVersionSelectOptions = useMemo((): MasterSelectOption[] => {
    return [...buildVersions]
      .sort((a, b) =>
        a.name.localeCompare(b.name, "ja", { sensitivity: "base" })
      )
      .map((m) => ({ value: m.name, label: m.name }));
  }, [buildVersions]);

  const templateFieldSelectOptions = useMemo(
    () => buildTemplateFieldSelectOptionsMap(issueTemplateItems),
    [issueTemplateItems]
  );

  useEffect(() => {
    if (!structuredReportSource || typeof structuredReportSource !== "object") {
      setEditableReport(null);
      return;
    }
    setEditableReport(
      mergeIncidentReportForDisplay(
        structuredReportSource as Record<string, unknown>
      )
    );
  }, [mergedReportFingerprint]);

  /** AI の名前/UUID を担当者一覧に合わせて UUID 化。未解決時はログインユーザーに突合 */
  useEffect(() => {
    if (assigneeUsers.length === 0) return;
    setEditableReport((prev) => {
      if (!prev) return prev;
      const resolved = resolveIssuePersonInChargeToAssigneeUserId({
        raw: prev.issue_person_in_charge,
        assignees: assigneeUsers,
        loginEmail: authUser?.email,
        loginName: authUser?.name,
      });
      if (!resolved) return prev;
      const curStr =
        typeof prev.issue_person_in_charge === "string"
          ? prev.issue_person_in_charge.trim()
          : "";
      if (curStr === resolved) return prev;
      return { ...prev, issue_person_in_charge: resolved };
    });
  }, [
    assigneeUsers,
    mergedReportFingerprint,
    authUser?.email,
    authUser?.name,
  ]);

  /** AI の自由記述を実行環境・ビルドのマスタ名称に寄せる */
  useEffect(() => {
    if (executionEnvironments.length === 0 && buildVersions.length === 0) {
      return;
    }
    setEditableReport((prev) => {
      if (!prev) return prev;
      let next: Record<string, unknown> | null = null;
      const patch = (key: string, val: string) => {
        if (!next) next = { ...prev };
        next[key] = [val];
      };
      if (executionEnvironments.length > 0) {
        const resolved = resolveReportValueToMasterName(
          prev.issue_test_execution_environment,
          executionEnvironments
        );
        const cur = Array.isArray(prev.issue_test_execution_environment)
          ? String(prev.issue_test_execution_environment[0] ?? "").trim()
          : typeof prev.issue_test_execution_environment === "string"
            ? prev.issue_test_execution_environment.trim()
            : "";
        if (resolved && resolved !== cur) {
          patch("issue_test_execution_environment", resolved);
        }
      }
      if (buildVersions.length > 0) {
        const resolved = resolveReportValueToMasterName(
          prev.issue_build_version,
          buildVersions
        );
        const cur = Array.isArray(prev.issue_build_version)
          ? String(prev.issue_build_version[0] ?? "").trim()
          : typeof prev.issue_build_version === "string"
            ? prev.issue_build_version.trim()
            : "";
        if (resolved && resolved !== cur) {
          patch("issue_build_version", resolved);
        }
      }
      return next ?? prev;
    });
  }, [executionEnvironments, buildVersions, mergedReportFingerprint]);

  /** AI の日本語ラベル（不具合 / A / 高 等）を課題テンプレート option の UUID に寄せる */
  useEffect(() => {
    if (issueTemplateItems.length === 0) return;
    const optionMap = buildTemplateFieldSelectOptionsMap(issueTemplateItems);
    setEditableReport((prev) => {
      if (!prev) return prev;
      let next: Record<string, unknown> | null = null;
      for (const fieldId of ISSUE_TEMPLATE_SELECT_FIELD_IDS) {
        const opts = optionMap[fieldId];
        if (!opts?.length) continue;
        const resolved = resolveReportValueToTemplateOptionId(
          prev[fieldId],
          opts
        );
        if (!resolved) continue;
        const rawField = prev[fieldId];
        const cur =
          typeof rawField === "string" ? rawField.trim() : "";
        if (cur === resolved) continue;
        if (!next) next = { ...prev };
        next[fieldId] = resolved;
      }
      return next ?? prev;
    });
  }, [issueTemplateItems, mergedReportFingerprint]);

  const handleReportFieldChange = useCallback((key: string, value: unknown) => {
    setEditableReport((prev) => {
      if (!prev) return prev;
      const next = { ...prev } as Record<string, unknown>;
      if (value === undefined) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  }, []);

  const formatOptions = [
    { value: "tfact", label: "TFACT" },
    // TODO: 一時的に非表示。後で復活させる
    // { value: "url", label: "URL Link" },
    // { value: "csv", label: "CSV" },
    // { value: "tsv", label: "TSV" },
    // { value: "json", label: "JSON" },
  ];

  const getCurrentReportData = (): any | null => {
    if (editableReport !== null && typeof editableReport === "object") {
      return editableReport;
    }
    return structuredReportSource || null;
  };

  // Check if report has valid data
  const hasValidReportData = (report: any): boolean => {
    if (!report) return false;
    
    if (typeof report === 'string') {
      return report.trim().length > 0;
    }
    
    if (typeof report === 'object') {
      const keys = Object.keys(report);
      if (keys.length === 0) return false;

      // Check if at least 1 field has a valid value
      return keys.some(key => {
        const value = report[key];
        if (value === null || value === undefined) return false;
        if (typeof value === 'string' && value.trim().length === 0) return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      });
    }
    
    return true;
  };


  const convertToDelimitedFormat = (
    data: Record<string, any>,
    delimiter: string
  ): string => {
    const rows: string[] = [];
    const headers = Object.keys(data || {});
    rows.push(headers.join(delimiter));
    const values = headers.map((header) => {
      let value = data?.[header] ?? "";
      value = typeof value === "string" ? value : JSON.stringify(value);
      if (
        value.includes(delimiter) ||
        value.includes("\n") ||
        value.includes('"')
      ) {
        value = `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    });
    rows.push(values.join(delimiter));
    return rows.join("\n");
  };

  const generateCSV = (data: Record<string, any>): string =>
    convertToDelimitedFormat(data, ",");
  const generateTSV = (data: Record<string, any>): string =>
    convertToDelimitedFormat(data, "\t");
  const generateJSON = (data: Record<string, any>): string =>
    JSON.stringify(data, null, 2);
  const generateUrlLink = (data: Record<string, any>): string => {
    const baseUrl = window.location.origin + window.location.pathname;
    const params = new URLSearchParams();
    if (currentProject?.id) params.append("project_id", currentProject.id);
    params.append("format", "url");
    params.append(
      "report_data",
      encodeURIComponent(JSON.stringify(data || {}))
    );
    return `${baseUrl}?${params.toString()}`;
  };

  const exportReportAs = async (format: "url" | "csv" | "tsv" | "json") => {
    const data = getCurrentReportData() || {};
    let exportContent = "";
    switch (format) {
      case "url":
        exportContent = generateUrlLink(data);
        break;
      case "csv":
        exportContent = generateCSV(data);
        break;
      case "tsv":
        exportContent = generateTSV(data);
        break;
      case "json":
      default:
        exportContent = generateJSON(data);
        break;
    }
    try {
      await navigator.clipboard.writeText(exportContent);
      addNotification({
        type: "success",
        title: "コピー完了",
        message: `選択した形式(${format.toUpperCase()})でクリップボードにコピーしました`,
      });
    } catch (err) {
      addNotification({
        type: "error",
        title: "コピー失敗",
        message: "クリップボードへのコピーに失敗しました",
      });
    }
  };

  const handleCreateIssue = async () => {
    if (!currentProject) {
      addNotification({
        type: "error",
        title: "課題作成不可",
        message: "プロジェクトが選択されていません",
      });
      return;
    }

    const currentDataRaw = getCurrentReportData();
    const currentData =
      currentDataRaw &&
      typeof currentDataRaw === "object" &&
      assigneeUsers.length > 0
        ? (() => {
            const resolved = resolveIssuePersonInChargeToAssigneeUserId({
              raw: (currentDataRaw as Record<string, unknown>)
                .issue_person_in_charge,
              assignees: assigneeUsers,
              loginEmail: authUser?.email,
              loginName: authUser?.name,
            });
            if (!resolved) return currentDataRaw;
            return {
              ...(currentDataRaw as Record<string, unknown>),
              issue_person_in_charge: resolved,
            };
          })()
        : currentDataRaw;
    const debugPrefix = "[ReportTab][TFACT]";
    try {
      console.groupCollapsed(`${debugPrefix} createIssue flow`);
    } catch {}
    try {
      console.log(`${debugPrefix} project`, currentProject);
      console.log(`${debugPrefix} incident_report raw`, currentData);
    } catch {}

    const workspaceId = currentProject.workspace_id;

    const tfactIdRegex = TFACT_ENTITY_ID_RE;

    let issueName: string | undefined = undefined;
    let issueValue = "{}";
    let issueStatusId: string | undefined = undefined;
    let issueTemplateItems: ReadonlyArray<Record<string, unknown>> = [];
    let firstAssigneeUserId: string | undefined = undefined;
    let projectAssigneeUserIds: Set<string> = new Set();

    const mapToOptionIds = (val: any, options: any[]): any => {
      if (!options || options.length === 0) return val;
      const matchOne = (v: any) => {
        if (typeof v !== "string") return v;
        if (tfactIdRegex.test(v) && options.some((o) => o.value === v)) return v;
        const hit = options.find((o) => {
          const dn = o.display_name || {};
          return dn.ja === v || dn.en === v;
        });
        return hit ? hit.value : v;
      };
      if (Array.isArray(val)) return val.map((v) => matchOne(v));
      return matchOne(val);
    };

    const buildValueWithTemplate = (
      tpl: any,
      reportObj: any,
      baseName: string | undefined,
      baseStatusId: string | undefined
    ) => {
      let resolvedName = baseName;
      let resolvedStatusId = baseStatusId;
      const normalized: Record<string, any> = {};
      const template =
        tpl?.template && typeof tpl.template === "string"
          ? (() => {
              try {
                return JSON.parse(tpl.template);
              } catch {
                return null;
              }
            })()
          : tpl?.template || tpl;
      if (!template || typeof template !== "object")
        return {
          normalized,
          statusId: baseStatusId,
          name: baseName,
          allItems: [] as any[],
        };

      const titleItems: any[] = Array.isArray(template.title)
        ? template.title
        : [];
      const fieldItems: any[] = Array.isArray(template.fields)
        ? template.fields
        : [];
      const attrItems: any[] = Array.isArray(template.attributes)
        ? template.attributes
        : [];
      const allItems: any[] = [...titleItems, ...fieldItems, ...attrItems];

      if (reportObj && typeof reportObj === "object" && reportObj.title) {
        resolvedName = String(reportObj.title);
      }

      for (const item of allItems) {
        const key = item?.id;
        if (!key || typeof key !== "string") continue;
        const required = !!(item?.validation && item.validation.required);
        let v =
          reportObj && typeof reportObj === "object"
            ? pickReportValueForTemplate(reportObj, key)
            : undefined;
        if (
          v === undefined ||
          v === null ||
          (typeof v === "string" && v.trim() === "")
        ) {
          if (required) v = item?.initial_value;
          else continue;
        }
        const options = item?.data?.options;
        if (Array.isArray(options)) v = mapToOptionIds(v, options);
        normalized[key] = v;
      }

      const statusCandidate =
        reportObj?.issue_status_id ||
        pickReportValueForTemplate(reportObj, "issue_status");
      if (
        typeof statusCandidate === "string" &&
        tfactIdRegex.test(statusCandidate)
      ) {
        resolvedStatusId = statusCandidate;
      } else {
        const statusDef = attrItems.find((a) => a?.id === "issue_status");
        if (statusDef) {
          const options = statusDef?.data?.options || [];
          if (typeof statusCandidate === "string") {
            const mapped = mapToOptionIds(statusCandidate, options);
            if (typeof mapped === "string" && tfactIdRegex.test(mapped))
              resolvedStatusId = mapped;
          }
          if (
            !resolvedStatusId &&
            statusDef.initial_value &&
            tfactIdRegex.test(statusDef.initial_value)
          ) {
            resolvedStatusId = statusDef.initial_value;
          }
        }
      }

      return {
        normalized,
        statusId: resolvedStatusId,
        name: resolvedName,
        allItems,
      };
    };

    setIsCreating(true);
    try {
      // fetch template and normalize required values
      let normalizedValue = issueValue;
      try {
        const tplRes = await TemplateAPI.getTemplate(
          "issue",
          currentProject.id
        );
        if (!tplRes.success || !tplRes.data) {
          const msg =
            (tplRes.error && tplRes.error.trim()) ||
            (tplRes.message && String(tplRes.message).trim()) ||
            "課題テンプレートを取得できませんでした。ネットワークまたは権限をご確認ください。";
          addNotification({
            type: "error",
            title: "課題作成不可",
            message: msg,
          });
          return;
        }
        const { normalized, statusId, name, allItems } =
          buildValueWithTemplate(
            tplRes.data,
            typeof currentData === "object" ? currentData : {},
            issueName,
            issueStatusId
          );
        issueTemplateItems = allItems ?? [];
        issueStatusId = statusId ?? issueStatusId;
        issueName = name ?? issueName;
        normalizedValue = JSON.stringify(normalized ?? {}, null, 2);
        if (process.env.NODE_ENV !== "development") {
          console.log(`${debugPrefix} normalized from template`, {
            issueStatusId,
            issueName,
            normalizedValue,
          });
        }
      } catch (err) {
        console.error(`${debugPrefix} template fetch failed`, err);
        addNotification({
          type: "error",
          title: "課題作成不可",
          message:
            err instanceof Error
              ? `テンプレート取得エラー: ${err.message}`
              : "課題テンプレートの取得に失敗しました。",
        });
        return;
      }

      if (!workspaceId) {
        addNotification({
          type: "error",
          title: "課題作成不可",
          message: "プロジェクトの workspace_id が見つかりません",
        });
        return;
      }

      if (!issueStatusId) {
        addNotification({
          type: "error",
          title: "課題作成不可",
          message: "テンプレートから課題ステータス初期値を取得できませんでした",
        });
        return;
      }

      if (
        !issueName ||
        (typeof issueName === "string" && issueName.trim() === "")
      ) {
        addNotification({
          type: "error",
          title: "課題作成不可",
          message: "課題名を取得できませんでした",
        });
        return;
      }

      // 追加解決: 実行環境/ビルドバージョンをBFFマスタからID解決
      let executionEnvironmentIds: string[] | undefined = undefined;
      let buildVersionIds: string[] | undefined = undefined;
      /** 暫定: ログインユーザーと担当者 UUID を突合するために保持（getAssignees の user 配列） */
      let assigneeUsersSnapshot: Array<Record<string, unknown>> = [];
      try {
        const [envRes, buildRes, assigneeRes] = await Promise.all([
          EnvironmentAPI.getExecutionEnvironments(currentProject.id),
          EnvironmentAPI.getBuildVersions(currentProject.id),
          ProjectAPI.getAssignees(currentProject.id),
        ]);

        const envMasters =
          envRes.success && envRes.data
            ? (envRes.data as any).test_execution_environments || []
            : [];
        const buildMasters =
          buildRes.success && buildRes.data
            ? (buildRes.data as any).build_versions || []
            : [];

        const normalizedObj = parseJsonObject(normalizedValue);
        const beforeEnvVal = normalizedObj["issue_test_execution_environment"];
        const beforeBuildVal = normalizedObj["issue_build_version"];
        const beforePersonVal = normalizedObj["issue_person_in_charge"];

        executionEnvironmentIds = resolveMasterIds(
          normalizedObj["issue_test_execution_environment"],
          envMasters,
          "execution_environment_id",
          "name"
        );
        buildVersionIds = resolveMasterIds(
          normalizedObj["issue_build_version"],
          buildMasters,
          "build_version_id",
          "name"
        );
        if (executionEnvironmentIds && executionEnvironmentIds.length === 0) {
          executionEnvironmentIds = undefined;
        }
        if (buildVersionIds && buildVersionIds.length === 0) {
          buildVersionIds = undefined;
        }

        // 担当者のID解決（issue_person_in_charge）→ CreateIssueInput側は将来拡張予定
        const rawAssigneeRows =
          assigneeRes.success && assigneeRes.data
            ? extractAssigneeUsersPayload(assigneeRes.data)
            : undefined;
        const assignees = Array.isArray(rawAssigneeRows) ? rawAssigneeRows : [];
        const users = assignees.map((a: any) => a?.user).filter(Boolean);
        assigneeUsersSnapshot = users;
        projectAssigneeUserIds = new Set();
        for (const u of users) {
          const uid = u?.id;
          if (typeof uid === "string" && tfactIdRegex.test(uid)) {
            projectAssigneeUserIds.add(uid);
          }
        }
        const firstUser = users[0];
        if (
          firstUser &&
          typeof firstUser.id === "string" &&
          tfactIdRegex.test(firstUser.id)
        ) {
          firstAssigneeUserId = firstUser.id;
        }
        const personVal = normalizedObj["issue_person_in_charge"];
        const personIds = resolveMasterIds(personVal, users, "id", "name");
        if (personIds && personIds.length > 0) {
          normalizedObj["issue_person_in_charge"] = personIds[0];
        } else if (firstAssigneeUserId) {
          normalizedObj["issue_person_in_charge"] = firstAssigneeUserId;
        } else if (
          personVal === "" ||
          personVal === null ||
          personVal === undefined
        ) {
          delete normalizedObj["issue_person_in_charge"];
        }

        normalizedValue = JSON.stringify(normalizedObj, null, 2);

        if (process.env.NODE_ENV !== "development") {
          console.log(`${debugPrefix} resolved mappings`, {
            envOriginal: beforeEnvVal,
            envIds: executionEnvironmentIds,
            buildOriginal: beforeBuildVal,
            buildIds: buildVersionIds,
            personOriginal: beforePersonVal,
            personId: normalizedObj["issue_person_in_charge"],
            normalizedValueAfterResolve: normalizedValue,
          });
        }
      } catch (envErr) {
        console.error(`${debugPrefix} env/build/assignee resolution failed`, envErr);
      }

      // Single canonicalization before submit (handles legacy keys even if env block failed).
      const finalNormalizedObj = (() => {
        try {
          return JSON.parse(normalizedValue || "{}") as Record<string, unknown>;
        } catch {
          return {} as Record<string, unknown>;
        }
      })();
      if (projectAssigneeUserIds.size === 0 && assigneeUsers.length > 0) {
        const fromState = buildProjectAssigneeResolution(
          assigneeUsers.map((u) => ({
            id: u.id,
            name: u.name,
            email: u.email,
          })),
          tfactIdRegex
        );
        fromState.projectAssigneeUserIds.forEach((id) =>
          projectAssigneeUserIds.add(id)
        );
        if (!firstAssigneeUserId) {
          firstAssigneeUserId = fromState.firstAssigneeUserId;
        }
        if (assigneeUsersSnapshot.length === 0) {
          assigneeUsersSnapshot = fromState.assigneeUsersSnapshot;
        }
      }
      if (projectAssigneeUserIds.size === 0) {
        try {
          const ar = await ProjectAPI.getAssignees(currentProject.id);
          if (ar.success && ar.data) {
            const userRows = extractAssigneeUserRows(ar.data);
            const fromApi = buildProjectAssigneeResolution(
              userRows,
              tfactIdRegex
            );
            fromApi.projectAssigneeUserIds.forEach((id) =>
              projectAssigneeUserIds.add(id)
            );
            if (!firstAssigneeUserId) {
              firstAssigneeUserId = fromApi.firstAssigneeUserId;
            }
            if (assigneeUsersSnapshot.length === 0) {
              assigneeUsersSnapshot = fromApi.assigneeUsersSnapshot;
            }
          }
        } catch {
          /* ignore */
        }
      }

      let submitValue = applyIssueValueCreatePipeline({
        issueValueObj: finalNormalizedObj,
        issueTemplateItems,
        uuidRegex: tfactIdRegex,
        projectAssigneeUserIds,
        firstAssigneeUserId,
        assigneeUsersSnapshot,
        loginEmail: authUser?.email,
        loginName: authUser?.name,
        requestingUserId: authUser?.id,
      });

      const missingRequired = findMissingRequiredIssueFields(
        submitValue,
        issueTemplateItems
      );
      if (missingRequired.length > 0) {
        addNotification({
          type: "error",
          title: "課題作成不可",
          message: formatMissingRequiredIssueFieldsMessageJa(
            missingRequired.map((m) => m.label)
          ),
        });
        return;
      }

      submitValue = appendSpecificationReportFieldsToRemarks(submitValue);
      submitValue = retainTfactIssueCustomSchemaProperties(submitValue);
      normalizedValue = JSON.stringify(submitValue, null, 2);

      const payload = {
        project_id: currentProject.id,
        workspace_id: workspaceId,
        issue_status_id: issueStatusId,
        name: issueName,
        value: normalizedValue,
        execution_environment_ids: executionEnvironmentIds,
        build_version_ids: buildVersionIds,
      };
      try {
        console.log(`${debugPrefix} createIssue payload`, payload);
      } catch {}
      const res = await IssueAPI.createIssue(payload);
      if (res.success && res.data) {
        const issueId = (res.data as any)?.issue?.issue_id || "";
        const tfactUrl = (res.data as any)?.tfact_url || null;

        addNotification({
          type: "success",
          title: "課題作成",
          message: issueId
            ? `課題を作成しました（ID: ${issueId}）`
            : "課題を作成しました",
          duration: tfactUrl ? 10000 : 5000,
          actions: tfactUrl
            ? [
                {
                  label: "課題を確認する",
                  onClick: () =>
                    window.open(tfactUrl, "_blank", "noopener,noreferrer"),
                  variant: "primary",
                },
              ]
            : undefined,
        });
      } else {
        addNotification({
          type: "error",
          title: "課題作成失敗",
          message: res.error || "課題の作成に失敗しました",
        });
      }
    } catch (error) {
      addNotification({
        type: "error",
        title: "課題作成失敗",
        message:
          error instanceof Error ? error.message : "課題の作成に失敗しました",
      });
    } finally {
      setIsCreating(false);
      try {
        console.groupEnd();
      } catch {}
    }
  };

  const handleGenerate = async () => {
    if (!currentProject) return;

    // 既にレポートがある場合は、選択したフォーマットでエクスポート（静的版と同様の挙動）
    if (getCurrentReportData()) {
      if (reportFormat === "tfact") {
        // TFACT 選択時は課題作成フローを実行
        await handleCreateIssue();
      } else {
        await exportReportAs(reportFormat as "url" | "csv" | "tsv" | "json");
      }
      return;
    }

    // レポートが未生成の場合は、チャット経由で生成を依頼
    // レポート未生成時
    // TFACT 選択時は課題作成ができないため、生成ボタンはdisableで制御
    setIsGenerating(true);
    try {
      await sendMessage(currentProject.id, "Please generate the report.", undefined, currentProject.workspace_id);
      addNotification({
        type: "success",
        title: "レポート生成開始",
        message: "AIにレポート生成を依頼しました。しばらくお待ちください。",
      });
    } catch (error) {
      addNotification({
        type: "error",
        title: "レポート生成失敗",
        message:
          error instanceof Error
            ? error.message
            : "レポートの生成に失敗しました",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Check if we have report data from chat or from API
  const reportDataFromChat = canShowReportFromChat ? reportState.report : null;
  const effectiveReportData = reportData || reportDataFromChat;
  const hasReportData = hasValidReportData(effectiveReportData);
  const canCreateIssue = Boolean(currentProject && hasReportData);

  const isHtmlReport = !reportData && canShowReportFromChat && typeof reportState.report === "string";
  const htmlReportContent: string = isHtmlReport
    ? String(reportState.report)
    : "";

  // Header state
  const headerButtonLoading =
    reportFormat === "tfact" ? isCreating : isGenerating;
  const headerButtonDisabled =
    reportFormat === "tfact"
      ? isCreating || !canCreateIssue
      : isGenerating || !currentProject;

  // Compose content
  let content: React.ReactNode;
  if (!hasReportData) {
    content = (
      <EmptyState>
        <EmptyStateIcon>
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 2v10h8V6H6z" />
            <path d="M8 8h4v2H8V8zm0 4h4v2H8v-2z" />
          </svg>
        </EmptyStateIcon>
        <EmptyStateTitle>レポート</EmptyStateTitle>
        <EmptyStateDescription>
          チャットでインシデントについて質問した後、「生成」ボタンをクリックしてレポートを作成してください。
        </EmptyStateDescription>
      </EmptyState>
    );
  } else if (isHtmlReport) {
    content = (
      <div
        style={{
          padding: "1rem",
          fontFamily: "inherit",
          lineHeight: "1.7",
          color: "inherit",
          height: "calc(100% - 80px)",
          overflowY: "auto",
        }}
        dangerouslySetInnerHTML={{ __html: htmlReportContent }}
      />
    );
  } else {
    content = (
      <ReportForm>
        <EditHint>
          課題登録の前に内容を確認し、必要に応じて各項目を編集してください。チャットでレポートが更新された場合、未保存の編集は新しい内容で置き換わります。
        </EditHint>
        <SchemaRenderer
          schema={INCIDENT_REPORT_RESPONSE_SCHEMA}
          data={editableReport ?? mergedReportForDisplay}
          editable={!!mergedReportForDisplay}
          onFieldChange={handleReportFieldChange}
          assigneeSelectOptions={assigneeSelectOptions}
          assigneesSelectLoading={assigneesLoading}
          executionEnvironmentSelectOptions={executionEnvironmentSelectOptions}
          executionEnvironmentsLoading={executionEnvironmentsLoading}
          buildVersionSelectOptions={buildVersionSelectOptions}
          buildVersionsLoading={buildVersionsLoading}
          templateFieldSelectOptions={templateFieldSelectOptions}
          templateFieldsLoading={issueTemplateLoading}
        />
      </ReportForm>
    );
  }

  return (
    <ReportContainer>
      <ReportHeader>
        <Select
          value={reportFormat}
          onChange={setReportFormat}
          options={formatOptions}
        />
        <Button
          onClick={handleGenerate}
          loading={headerButtonLoading}
          disabled={headerButtonDisabled}
        >
          生成
        </Button>
      </ReportHeader>
      {content}
    </ReportContainer>
  );
};

export default ReportTab;
